import {
  generateCreativeTemplate,
  getCreativeTemplateSignedUrl,
  uploadCreativeTemplateAsset,
} from "@/app/_api/creativeTemplates";
import type { InteractiveImageGenerationInput } from "@ui/components/demos/InteractiveImage";

/** Successful generation, with the extra metadata {@link InteractiveImageLive} needs to build the
 *  "claim your asset" deep link (model + refined prompt) on top of the displayable result. */
interface InteractiveImageGenerationOutcome {
  imageUrl: string;
  alt: string;
  assetId: string;
  modelId: string | null;
  refinedPrompt: string | null;
}

interface BuildOnGenerateInput extends InteractiveImageGenerationInput {
  /**
   * Invoked once the input/source asset id is resolved (chained from a prior result or freshly
   * uploaded), so a later generation failure (e.g. quota) can still deep-link the gated CTA with
   * it — the producer can't otherwise see an asset uploaded inside this call.
   */
  onSourceAssetResolved?: (assetId: string) => void;
}

/**
 * Builds the generation callback for InteractiveImage create mode against the legacy anonymous
 * creative-templates API. Pure and React-free so it can be unit-tested.
 *
 * The single `asset_id` slot is filled by precedence: a prior result chained by its `assetId` (no
 * re-upload) when editing, else the uploaded reference file on a first generation. A source picks
 * the EDITION template, none picks CREATION; the result carries `asset_id` so edits can chain on.
 */
export function buildOnGenerate({
  creationTemplateSlug,
  editionTemplateSlug,
}: {
  creationTemplateSlug: string;
  editionTemplateSlug: string;
}) {
  return async ({
    prompt,
    attachments,
    baseImageAssetId,
    signal,
    onSourceAssetResolved,
  }: BuildOnGenerateInput): Promise<InteractiveImageGenerationOutcome> => {
    let sourceAssetId = baseImageAssetId;
    if (!sourceAssetId && attachments[0]) {
      const upload = await uploadCreativeTemplateAsset(attachments[0], signal);
      sourceAssetId = upload.asset_id;
    }
    // Surface the input asset before generating: a quota failure happens on generate (after upload),
    // and the gated CTA still wants to carry it.
    if (sourceAssetId) onSourceAssetResolved?.(sourceAssetId);

    const templateSlug = sourceAssetId ? editionTemplateSlug : creationTemplateSlug;
    const generated = await generateCreativeTemplate({
      templateSlug,
      assetId: sourceAssetId,
      userInput: prompt,
      signal,
    });
    if (!generated.asset_id) {
      throw new Error("[InteractiveImageLive] generate returned no asset_id");
    }

    const signed = await getCreativeTemplateSignedUrl(generated.asset_id, signal);
    return {
      imageUrl: signed.url,
      alt: prompt,
      assetId: generated.asset_id,
      modelId: generated.model_id,
      refinedPrompt: generated.refined_prompt,
    };
  };
}
